/**
 * useSplitterDrag (CH-13)
 *
 * Shared pointer/keyboard mechanics for a drag-resize splitter. Used by
 * every app-shell splitter (MainShell's sidebar seam, the sidebar's
 * Explorer/Scripts divider, the vertical-tabs column seam) so the three
 * sites behave identically rather than each re-implementing (and subtly
 * diverging on) the same drag plumbing.
 *
 * This hook only owns the mechanical parts that are identical everywhere a
 * splitter appears:
 *   - `setPointerCapture` so a fast drag or one that outruns the window
 *     doesn't drop the resize partway through.
 *   - rAF-batched move handling so `pointermove` can't thrash layout more
 *     than once per frame.
 *   - Arrow-key stepping and double-click reset wiring.
 *
 * The caller owns the domain math — what a "delta" or "step" means in its
 * own units (px width, a 0..1 ratio, ...) — and when/how to persist it.
 * Deliberately NOT a general-purpose splitter framework: no min/max
 * clamping, no persistence, no rendering. Just the three call sites' shared
 * mechanics.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type SplitterAxis = 'x' | 'y';

export interface UseSplitterDragOptions {
  /** Screen axis the pointer moves along to resize: 'x' = drag left/right, 'y' = drag up/down. */
  axis: SplitterAxis;
  /** rAF-batched: the signed pixel delta since the pointer went down. */
  onDrag: (deltaPx: number) => void;
  /** Called once when the drag ends (pointer up/cancel) — e.g. to persist a live-mutated value. */
  onDragEnd?: () => void;
  /** Double-click resets the split to its default position. */
  onReset: () => void;
  /** Arrow-key step. `direction` is -1 (decrease) / 1 (increase); `big` is true when Shift is held. */
  onKeyStep: (direction: -1 | 1, big: boolean) => void;
}

export interface UseSplitterDragResult {
  /** True while a pointer drag is in progress — drive an active/dragging visual state from this. */
  isDragging: boolean;
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onDoubleClick: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
}

export function useSplitterDrag({
  axis,
  onDrag,
  onDragEnd,
  onReset,
  onKeyStep,
}: UseSplitterDragOptions): UseSplitterDragResult {
  const [isDragging, setIsDragging] = useState(false);
  const rafRef = useRef<number | null>(null);
  const pendingDeltaRef = useRef<number | null>(null);
  const startPosRef = useRef(0);

  const flush = useCallback(() => {
    rafRef.current = null;
    if (pendingDeltaRef.current !== null) {
      onDrag(pendingDeltaRef.current);
      pendingDeltaRef.current = null;
    }
  }, [onDrag]);

  // Structural guard (fix round 1, Finding 6): if the splitter unmounts
  // mid-drag, a scheduled rAF must never fire afterward — it would call
  // `onDrag` with a stale closure over refs the caller may no longer expect
  // to be live. Previously this was only incidentally safe because every
  // call site's `onDrag` happened to null-check its own DOM ref; with ~14
  // more sites adopting this hook, the guarantee belongs here once instead
  // of depending on every adopter remembering the same defensive check.
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      // Left button / primary touch only.
      if (e.button !== 0) return;
      e.preventDefault();
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);
      startPosRef.current = axis === 'x' ? e.clientX : e.clientY;
      setIsDragging(true);

      // Guards `finishDrag` against running twice for the same drag. Without
      // it, a `lostpointercapture` backstop (below) firing alongside a normal
      // `pointerup`/`pointercancel` would double-invoke `onDragEnd` and the
      // pending-delta flush.
      let finished = false;

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const pos = axis === 'x' ? moveEvent.clientX : moveEvent.clientY;
        pendingDeltaRef.current = pos - startPosRef.current;
        if (rafRef.current === null) {
          rafRef.current = requestAnimationFrame(flush);
        }
      };

      const finishDrag = (upEvent: PointerEvent) => {
        if (finished) return;
        finished = true;
        try {
          target.releasePointerCapture(upEvent.pointerId);
        } catch {
          // Capture may already be gone (e.g. the element unmounted mid-drag,
          // or this run IS the `lostpointercapture` backstop, where capture
          // is by definition already released).
        }
        target.removeEventListener('pointermove', handlePointerMove);
        target.removeEventListener('pointerup', finishDrag);
        target.removeEventListener('pointercancel', finishDrag);
        target.removeEventListener('lostpointercapture', finishDrag);
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        // Flush any delta that hasn't made it through an rAF frame yet, so
        // the final pointer position is always respected exactly.
        if (pendingDeltaRef.current !== null) {
          onDrag(pendingDeltaRef.current);
          pendingDeltaRef.current = null;
        }
        setIsDragging(false);
        onDragEnd?.();
      };

      target.addEventListener('pointermove', handlePointerMove);
      target.addEventListener('pointerup', finishDrag);
      target.addEventListener('pointercancel', finishDrag);
      // Backstop (fix round 1, Finding 5): some browsers can implicitly
      // revoke pointer capture (window-blur / system-gesture edge cases)
      // without ever firing `pointerup`/`pointercancel`. Left uncaught, the
      // hook's `isDragging` state and its listeners on `target` would stay
      // wedged until the next unrelated pointerdown — a leaked-capture-
      // adjacent failure far worse than a janky drag.
      target.addEventListener('lostpointercapture', finishDrag);
    },
    [axis, flush, onDrag, onDragEnd]
  );

  const onDoubleClick = useCallback(() => {
    onReset();
  }, [onReset]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      const decreaseKey = axis === 'x' ? 'ArrowLeft' : 'ArrowUp';
      const increaseKey = axis === 'x' ? 'ArrowRight' : 'ArrowDown';
      if (e.key === decreaseKey) {
        e.preventDefault();
        onKeyStep(-1, e.shiftKey);
      } else if (e.key === increaseKey) {
        e.preventDefault();
        onKeyStep(1, e.shiftKey);
      }
    },
    [axis, onKeyStep]
  );

  return { isDragging, onPointerDown, onDoubleClick, onKeyDown };
}
